<template>
  <div class="groups-container">
    <!-- Main Content Wrapper -->
    <div class="groups-content">
      <!-- Sidebar -->
      <div class="sidebar">
        <div class="sidebar-header">
          <h2>Chat Groups</h2>
          <el-button 
            v-if="userHasBadge('moder')"
            @click="showCreateGroupModal = true" 
            type="primary" 
            size="small"
            circle
            class="add-group-btn"
          >
            <i class="fas fa-plus"></i>
          </el-button>
        </div>

        <div class="group-list">
          <!-- User Groups Section -->
          <div class="group-section" v-if="userGroups.length > 0">
            <h3 class="section-title">Your Groups</h3>
            <div class="group-grid">
              <div 
                v-for="group in userGroups" 
                :key="'user-'+group.id"
                class="group-card"
                :class="{active: activeGroup && activeGroup.id === group.id}"
                @click="selectGroup(group)"
              >
              <div class="group-avatar">
  <img 
    v-if="group.image_path" 
    :src="getGroupImageUrl(group.image_path)" 
    class="avatar-image"
  >
  <div v-else class="avatar-circle">
    <i class="fas fa-users"></i>
  </div>
  <div class="unread-badge" v-if="group.unread_count > 0">
    {{ group.unread_count }}
  </div>
</div>
                <div class="group-details">
                  <h4>{{ group.name }}</h4>
                  <p class="group-description">{{ group.description || 'No description' }}</p>
                  <div class="group-meta">
                    <span class="member-count">
                      <i class="fas fa-user-friends"></i> {{ group.members?.length || 0 }}
                    </span>
                    <span class="last-message" v-if="group.last_message">
                      {{ formatMessagePreview(group.last_message) }}
                    </span>
                  </div>
                </div>
                <div class="group-badges">
                  <el-tag v-if="isGroupAdmin(group)" size="mini" type="success">Admin</el-tag>
                  <el-tag v-if="group.is_public" size="mini" type="info">Public</el-tag>
                </div>
              </div>
            </div>
          </div>

          <!-- Public Groups Section -->
          <div class="group-section" v-if="publicGroups.length > 0">
            <h3 class="section-title">Public Groups</h3>
            <div class="group-grid">
              <div 
                v-for="group in publicGroups" 
                :key="'public-'+group.id"
                class="group-card"
                :class="{active: activeGroup && activeGroup.id === group.id}"
                @click="selectGroup(group)"
              >
              <div class="group-avatar">
                <img 
  v-if="group.image_path" 
  :src="getGroupImageUrl(group.image_path)" 
  class="avatar-image"
/>

  <div v-else class="avatar-circle">
    <i class="fas fa-users"></i>
  </div>
</div>
                <div class="group-details">
                  <h4>{{ group.name }}</h4>
                  <p class="group-description">{{ group.description || 'No description' }}</p>
                  <div class="group-meta">
                    <span class="member-count">
                      <i class="fas fa-user-friends"></i> {{ group.members?.length || 0 }}
                    </span>
                  </div>
                </div>
                <div class="group-badges">
                  <el-tag v-if="isGroupAdmin(group)" size="mini" type="success">Admin</el-tag>
                  <el-tag v-if="group.is_public" size="mini" type="info">Public</el-tag>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Area -->
      <div class="chat-area" v-if="activeGroup">
        <div class="chat-header">
          <div class="header-left">
            <div class="group-avatar">
              <div class="avatar-circle">
                <i class="fas fa-users"></i>
              </div>
            </div>
            <div class="group-info">
              <h3>{{ activeGroup.name }}</h3>
              <p class="member-count">
                {{ activeGroup.members?.length || 0 }} members
                <span v-if="activeGroup.class">• Class: {{ activeGroup.class.name }}</span>
                <span v-if="activeGroup.section">• Section: {{ activeGroup.section.name }}</span>
              </p>
            </div>
          </div>
          <div class="header-actions">
            <el-button-group>
              <el-button 
                @click="showGroupInfo = true" 
                size="small"
                plain
                class="header-action-btn"
              >
                <i class="fas fa-info-circle"></i> Info
              </el-button>
              <el-button 
                v-if="isGroupAdmin(activeGroup) || userHasBadge('moder')" 
                @click="showManageMembers = true" 
                size="small"
                type="primary"
                plain
                class="header-action-btn"
              >
                <i class="fas fa-users-cog"></i> Manage
              </el-button>
              <!-- <el-button 
                @click="leaveGroup(activeGroup)" 
                v-if="!isGroupAdmin(activeGroup) && !userHasBadge('moder')" 
                size="small"
                type="warning"
                plain
                class="header-action-btn"
              >
                <i class="fas fa-sign-out-alt"></i> Leave
              </el-button> -->
              <!-- <el-button 
                @click="deleteGroup(activeGroup)" 
                v-if="activeGroup.creator_id === currentUser.id || userHasBadge('moder')" 
                size="small"
                type="danger"
                plain
                class="header-action-btn"
              >
                <i class="fas fa-trash"></i> Delete
              </el-button> -->
            </el-button-group>
          </div>
        </div>
        
        <!-- Messages Container -->
        <div class="messages-container" ref="messagesContainer">
          <div 
            v-for="message in messages" 
            :key="message.id" 
            class="message" 
            :class="{
              outgoing: message.sender_id === currentUser.id,
              'has-media': message.message_type !== 'text'
            }"
          >
            <div class="message-avatar" v-if="message.sender_id !== currentUser.id">
              <div class="avatar-circle">
                <img v-if="message.sender.avatar" :src="message.sender.avatar" :alt="message.sender.name">
                <i v-else class="fas fa-user"></i>
              </div>
            </div>
            
            <div class="message-content-wrapper">
              <div class="message-content">
                <div class="message-header" v-if="message.sender_id !== currentUser.id">
                  <strong>{{ message.sender.name }}</strong>
                </div>
                <div class="message-body">
                  <p v-if="message.content && message.content.trim() !== ''">{{ message.content }}</p>
                  
                  <img 
                    v-if="message.message_type === 'image'" 
                    :src="getMediaUrl(message.media_path)" 
                    class="message-media"
                    @click="showMediaViewer(message.media_path)"
                  >
                  <video 
                    v-else-if="message.message_type === 'video'" 
                    controls 
                    class="message-media"
                  >
                    <source :src="getMediaUrl(message.media_path)" :type="getVideoMimeType(message.media_path)">
                  </video>
                  <audio 
                    v-else-if="message.message_type === 'audio'" 
                    controls 
                    class="message-media"
                  >
                    <source :src="getMediaUrl(message.media_path)" :type="getAudioMimeType(message.media_path)">
                  </audio>
                  <div 
                    v-else-if="message.message_type === 'document'" 
                    class="document-message"
                  >
                    <el-button type="text" :href="getMediaUrl(message.media_path)" target="_blank">
                      <i class="fas fa-file-download"></i> Download Document
                    </el-button>
                  </div>
                </div>
                <div class="message-time">
                  {{ formatTime(message.created_at) }}
                  <i v-if="message.sender_id === currentUser.id" class="fas fa-check-double read-receipt"></i>
                </div>
              </div>
            </div>
            
            <div class="message-actions" v-if="message.sender_id === currentUser.id || userHasBadge('moder')">
              <el-button 
                @click="deleteMessage(message)" 
                type="text"
                size="mini"
                icon="el-icon-delete"
                class="message-action-btn"
              ></el-button>
            </div>
          </div>
        </div>
        
        <div class="message-input-area">
          <div class="file-previews" v-if="selectedFile">
            <div class="file-preview">
              <div class="file-preview-content">
                <img 
                  v-if="selectedFile.type.startsWith('image/')" 
                  :src="filePreviewUrl" 
                  class="preview-media"
                />
                <video 
                  v-else-if="selectedFile.type.startsWith('video/')" 
                  class="preview-media"
                >
                  <source :src="filePreviewUrl" :type="selectedFile.type">
                </video>
                <div v-else class="document-preview">
                  <i class="fas fa-file-alt"></i>
                  <span>{{ selectedFile.name }}</span>
                </div>
              </div>
              <button class="remove-file-btn" @click="removeFile">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <div class="voice-recording-ui" v-if="isRecording">
      <div class="recording-indicator">
        <div class="pulse-animation"></div>
        <span class="recording-text">Recording...</span>
        <span class="recording-duration">{{ formatRecordingTime(recordingTime) }}</span>
      </div>
      <div class="recording-controls">
        <button class="cancel-recording" @click="cancelRecording">
          <i class="fas fa-times"></i>
        </button>
        <button class="send-recording" @click="stopAndSendRecording">
          <i class="fas fa-check"></i>
        </button>
      </div>
    </div>
    
    <div class="input-wrapper" v-if="!isRecording">
      <button class="emoji-button" @click="toggleEmojiPicker">
        <i class="far fa-smile"></i>
      </button>
      
      <div class="emoji-picker-container" v-if="emojiPickerVisible">
        <Picker 
          :data="emojiIndex" 
          set="twitter" 
          @select="addEmoji"
          title="Pick your emoji"
          emoji="point_up"
          :showPreview="false"
        />
      </div>
      
      <input
        v-model="newMessage"
        @keyup.enter="sendMessage"
        placeholder="Type a message..."
        class="message-input"
        ref="messageInput"
        @keydown.space="handleSpaceKey"
      />
      
      <button class="attachment-button" @click="triggerFileInput">
        <i class="fas fa-paperclip"></i>
      </button>
      
      <button 
        class="voice-button" 
        @mousedown="startRecording"
        @touchstart="startRecording"
        @mouseup="stopRecording"
        @touchend="stopRecording"
        @mouseleave="cancelIfRecording"
      >
        <i class="fas fa-microphone"></i>
      </button>
      
      <button 
        class="send-button" 
        @click="sendMessage" 
        :disabled="!newMessage.trim() && !selectedFile"
      >
        <i class="fas fa-paper-plane"></i>
      </button>
    </div>
    
    <input 
      ref="fileInput" 
      type="file" 
      @change="handleFileUpload" 
      style="display: none;"
      accept="image/*, video/*, .pdf, .doc, .docx, .xls, .xlsx"
    >
  </div>
      </div>
      
      <!-- Empty State -->
      <div class="empty-chat" v-if="!activeGroup">
        <div class="empty-content">
          <div class="empty-icon">
            <i class="fas fa-comments"></i>
          </div>
          <h3>Select a group to start chatting</h3>
          <p>Or create a new group if you're a moderator</p>
          <el-button 
            v-if="userHasBadge('moder')"
            @click="showCreateGroupModal = true" 
            type="primary"
            class="create-group-btn"
          >
            <i class="fas fa-plus"></i> Create Group
          </el-button>
        </div>
      </div>
    </div>
    
    <!-- Modals -->
    <GroupInfoModal 
      v-if="showGroupInfo" 
      :group="activeGroup" 
      @close="showGroupInfo = false"
    />
    
    <CreateGroupModal 
      v-if="showCreateGroupModal" 
      :classes="classes"
      :filteredSections="filteredSections"
      @close="showCreateGroupModal = false"
      @create="createGroup"
    />
    
    <ManageMembersModal 
      v-if="showManageMembers" 
      :group="activeGroup"
      :classes="classes"
      @close="showManageMembers = false"
      @group-updated="handleGroupUpdated"
    />
    
    <MediaViewerModal 
      v-if="showMediaViewerModal" 
      :mediaPath="viewedMediaPath" 
      :mediaType="viewedMediaType"
      @close="showMediaViewerModal = false"
    />
  </div>
</template>

<script>
import axios from 'axios';
import data from 'emoji-mart-vue-fast/data/all.json';
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast/src';
import "emoji-mart-vue-fast/css/emoji-mart.css";

// Components
import GroupInfoModal from './GroupInfoModal.vue';
import CreateGroupModal from './CreateGroupModal.vue';
import ManageMembersModal from './ManageMembersModal.vue';
import MediaViewerModal from './MediaViewerModal.vue';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

export default {
  components: {
    GroupInfoModal,
    CreateGroupModal,
    ManageMembersModal,
    MediaViewerModal,
    Picker,
  },
  
  data() {
    const getUserData = () => {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) return {};
        return typeof userData === 'object' ? userData : JSON.parse(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return {};
      }
    };

    return {
       // Existing data properties...
       isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      recordingTime: 0,
      recordingTimer: null,
      audioContext: null,
      audioStream: null,

      emojiIndex: new EmojiIndex(data),
      publicGroups: [],
      userGroups: [],
      activeGroup: null,
      filePreviewUrl: null,
      messages: [],
      newMessage: '',
      newGroup: {
        name: '',
        description: '',
        is_public: false,
        class_id: null,
        section_id: null
      },
      classes: [],
      sections: [],
      showCreateGroupModal: false,
      showGroupInfo: false,
      showManageMembers: false,
      memberSearch: '',
      searchResults: [],
      emojiPickerVisible: false,
      selectedFile: null,
      showMediaViewerModal: false,
      viewedMediaPath: '',
      viewedMediaType: '',
      currentUser: getUserData(),
      pollingInterval: null,
      messagePollingInterval: null
    };
  },

  computed: {
    filteredSections() {
      if (!this.newGroup.class_id) return [];
      return this.sections.filter(section => section.class_id == this.newGroup.class_id);
    }
  },

  created() {
    console.log("Current User:", this.currentUser);
    this.fetchGroups();
    this.fetchClasses();
    this.fetchSections();
    this.setupFirebaseListeners();
    this.pollingInterval = setInterval(this.fetchGroups, 30000);
  },

  beforeUnmount() {
    clearInterval(this.pollingInterval);
    clearInterval(this.messagePollingInterval);
    document.removeEventListener('click', this.handleClickOutsideEmojiPicker);
    if (this.filePreviewUrl) {
      URL.revokeObjectURL(this.filePreviewUrl);
    }
  },

  watch: {
    activeGroup(newVal) {
      if (newVal) {
        this.fetchMessages();
        clearInterval(this.messagePollingInterval);
        this.messagePollingInterval = setInterval(this.fetchMessages, 10000);
      } else {
        clearInterval(this.messagePollingInterval);
      }
    }
  },

  methods: {

    getGroupImageUrl(path) {
    if (!path) return '';
    
    // If the path is already a full URL, return it as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    
    // Otherwise, construct the full URL
    return `${process.env.VUE_APP_API_BASE_URL_MEDIA || 'https://cale.caledonian.ly'}/storage/${path}`;
  },

    async startRecording() {
      try {
        // Check if we're already recording
        if (this.isRecording) return;
        
        // Reset recording state
        this.isRecording = true;
        this.audioChunks = [];
        this.recordingTime = 0;
        
        // Request microphone access
        this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Create audio context and media recorder
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.mediaRecorder = new MediaRecorder(this.audioStream);
        
        // Setup event handlers
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };
        
        this.mediaRecorder.onstop = () => {
          // Handle in stopRecording method
        };
        
        // Start recording
        this.mediaRecorder.start(100); // Collect data every 100ms
        
        // Start timer
        this.recordingTimer = setInterval(() => {
          this.recordingTime += 1;
        }, 1000);
        
      } catch (error) {
        console.error('Error starting recording:', error);
        this.cancelRecording();
        this.$message.error('Could not access microphone. Please check permissions.');
      }
    },
    
    async stopRecording() {
      if (!this.isRecording) return;
      
      clearInterval(this.recordingTimer);
      
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
      }
      
      // If recording was very short, treat as cancelled
      if (this.recordingTime < 1) {
        this.cancelRecording();
        return;
      }
    },
    
    async stopAndSendRecording() {
      await this.stopRecording();
      
      // Combine audio chunks into a single blob
      const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
      
      // Create a File object from the Blob
      const audioFile = new File([audioBlob], `voice-message-${Date.now()}.aac`, {
        type: 'audio/webm'
      });
      
      // Prepare form data
      const formData = new FormData();
      formData.append('media', audioFile);
      
      try {
        const response = await api.post(
          `/chat-groups/${this.activeGroup.id}/messages`, 
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        this.messages.push(response.data);
        this.$nextTick(this.scrollToBottom);
      } catch (error) {
        console.error('Error sending voice message:', error);
        this.$message.error('Failed to send voice message');
      } finally {
        this.resetRecordingState();
      }
    },
    
    cancelRecording() {
      clearInterval(this.recordingTimer);
      
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
      }
      
      this.resetRecordingState();
    },
    
    cancelIfRecording() {
      if (this.isRecording) {
        this.cancelRecording();
      }
    },
    
    resetRecordingState() {
      this.isRecording = false;
      this.audioChunks = [];
      this.recordingTime = 0;
      this.mediaRecorder = null;
      this.audioStream = null;
      this.audioContext = null;
    },
    
    formatRecordingTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    },
    
    handleSpaceKey(e) {
      if (e.target === this.$refs.messageInput && !this.newMessage.trim()) {
        e.preventDefault(); // Prevent space from being entered
        this.startRecording();
      }
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (this.filePreviewUrl) {
        URL.revokeObjectURL(this.filePreviewUrl);
      }
      
      this.selectedFile = file;
      
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        this.filePreviewUrl = URL.createObjectURL(file);
      } else {
        this.filePreviewUrl = null;
      }
      
      this.$nextTick(() => {
        this.$refs.messageInput.focus();
      });
    },
    
    removeFile() {
      this.selectedFile = null;
      this.filePreviewUrl = null;
      this.$refs.fileInput.value = '';
    },

    handleGroupUpdated(updatedGroup) {
      this.activeGroup = updatedGroup;
      const index = this.userGroups.findIndex(g => g.id === updatedGroup.id);
      if (index !== -1) {
        this.userGroups.splice(index, 1, updatedGroup);
      }
    },

    async fetchGroups() {
      try {
        const response = await api.get('/chat-groups');
        this.publicGroups = response.data?.public_groups || [];
        this.userGroups = response.data?.user_groups || [];

        await Promise.all(this.userGroups.map(async group => {
          if (group.id === this.activeGroup?.id) return;
          
          try {
            const [msgResponse, unreadResponse] = await Promise.all([
              api.get(`/chat-groups/${group.id}/messages?limit=1`),
              api.get(`/chat-groups/${group.id}/unread-count`)
            ]);
            
            if (msgResponse.data?.data?.[0]) {
              group.last_message = msgResponse.data.data[0];
            }
            group.unread_count = unreadResponse.data?.count || 0;
          } catch (error) {
            console.error('Error fetching group details:', error);
          }
        }));
      } catch (error) {
        console.error('Error fetching groups:', error);
        this.publicGroups = [];
        this.userGroups = [];
      }
    },

    async fetchClasses() {
      try {
        const response = await api.get('/classes');
        this.classes = response.data || [];
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    },

    async fetchSections() {
      try {
        const response = await api.get('/sections');
        this.sections = response.data || [];
      } catch (error) {
        console.error('Error fetching sections:', error);
      }
    },

    async fetchMessages() {
      if (!this.activeGroup) return;
      
      try {
        const response = await api.get(`/chat-groups/${this.activeGroup.id}/messages`);
        this.messages = response.data?.data?.reverse() || [];
        this.$nextTick(this.scrollToBottom);
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    },

    async selectGroup(group) {
      try {
        const response = await api.get(`/chat-groups/${group.id}`);
        this.activeGroup = response.data;
        this.fetchMessages();
      } catch (error) {
        console.error('Error selecting group:', error);
      }
    },

    async sendMessage() {
      if (!this.newMessage.trim() && !this.selectedFile) return;
      
      const formData = new FormData();
      if (this.newMessage.trim()) formData.append('content', this.newMessage);
      if (this.selectedFile) formData.append('media', this.selectedFile);
      
      try {
        const response = await api.post(
          `/chat-groups/${this.activeGroup.id}/messages`, 
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        this.messages.push(response.data);
        this.newMessage = '';
        this.removeFile();
        this.emojiPickerVisible = false;
        this.$nextTick(this.scrollToBottom);
      } catch (error) {
        console.error('Error sending message:', error);
      }
    },

    async createGroup(groupData) {
      try {
        const response = await api.post('/chat-groups', groupData);
        this.userGroups.push(response.data);
        this.activeGroup = response.data;
        this.showCreateGroupModal = false;
        this.newGroup = {
          name: '',
          description: '',
          is_public: false,
          class_id: null,
          section_id: null
        };
      } catch (error) {
        console.error('Error creating group:', error);
      }
    },

    async deleteGroup(group) {
      if (!confirm(`Are you sure you want to delete "${group.name}"?`)) return;
      
      try {
        await api.delete(`/chat-groups/${group.id}`);
        if (this.activeGroup?.id === group.id) this.activeGroup = null;
        this.userGroups = this.userGroups.filter(g => g.id !== group.id);
      } catch (error) {
        console.error('Error deleting group:', error);
      }
    },

    async leaveGroup(group) {
      if (!confirm(`Are you sure you want to leave "${group.name}"?`)) return;
      
      try {
        await api.post(`/chat-groups/${group.id}/leave`);
        if (this.activeGroup?.id === group.id) this.activeGroup = null;
        this.userGroups = this.userGroups.filter(g => g.id !== group.id);
        this.publicGroups.push(group);
      } catch (error) {
        console.error('Error leaving group:', error);
      }
    },

    async addMember(user) {
      try {
        await api.post(`/chat-groups/${this.activeGroup.id}/members`, {
          user_ids: [user.id]
        });
        const response = await api.get(`/chat-groups/${this.activeGroup.id}`);
        this.activeGroup = response.data;
      } catch (error) {
        console.error('Error adding member:', error);
      }
    },

    async removeMember(member) {
      if (!confirm(`Remove ${member.name} from group?`)) return;
      
      try {
        await api.post(`/chat-groups/${this.activeGroup.id}/remove-members`, {
          user_ids: [member.id]
        });
        const response = await api.get(`/chat-groups/${this.activeGroup.id}`);
        this.activeGroup = response.data;
      } catch (error) {
        console.error('Error removing member:', error);
      }
    },

    async blockMember(member, block) {
      const action = block ? 'block' : 'unblock';
      if (!confirm(`${action} ${member.name}?`)) return;
      
      try {
        await api.post(`/chat-groups/${this.activeGroup.id}/block-members`, {
          user_ids: [member.id],
          block: block
        });
        const response = await api.get(`/chat-groups/${this.activeGroup.id}`);
        this.activeGroup = response.data;
      } catch (error) {
        console.error('Error blocking member:', error);
      }
    },

    async updateMemberRole(member, asAdmin) {
      const action = asAdmin ? 'promote' : 'demote';
      if (!confirm(`${action} ${member.name}?`)) return;
      
      try {
        await api.post(`/chat-groups/${this.activeGroup.id}/update-role`, {
          user_ids: [member.id],
          as_admin: asAdmin
        });
        const response = await api.get(`/chat-groups/${this.activeGroup.id}`);
        this.activeGroup = response.data;
      } catch (error) {
        console.error('Error updating member role:', error);
      }
    },

    async deleteMessage(message) {
      if (!confirm('Delete this message?')) return;
      
      try {
        await api.delete(`/messages/${message.id}`);
        this.messages = this.messages.filter(m => m.id !== message.id);
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) container.scrollTop = container.scrollHeight;
    },

    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    },

    formatMessagePreview(message) {
      if (!message) return '';
      return message.message_type === 'text' 
        ? (message.content.length > 30 ? message.content.substring(0, 30) + '...' : message.content)
        : `[${message.message_type}]`;
    },

    toggleEmojiPicker() {
      this.emojiPickerVisible = !this.emojiPickerVisible;
      if (this.emojiPickerVisible) {
        this.$nextTick(() => {
          this.$refs.messageInput.focus();
        });
      }
    },

    addEmoji(emoji) {
      if (emoji && emoji.native) {
        const input = this.$refs.messageInput;
        const startPos = input.selectionStart;
        const endPos = input.selectionEnd;
        
        this.newMessage = 
          this.newMessage.substring(0, startPos) + 
          emoji.native + 
          this.newMessage.substring(endPos);
        
        this.$nextTick(() => {
          input.selectionStart = input.selectionEnd = startPos + emoji.native.length;
          input.focus();
        });
      }
    },

    handleClickOutsideEmojiPicker(event) {
      const emojiPicker = this.$el.querySelector('.emoji-picker-container');
      if (emojiPicker && !emojiPicker.contains(event.target) && 
          !this.$el.querySelector('.emoji-button').contains(event.target)) {
        this.emojiPickerVisible = false;
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    getMediaUrl(path) {
      return path ? `${ 'https://cale.caledonian.ly'}/storage/${path}` : '';
    },

    showMediaViewer(path) {
      if (!path) return;
      
      const extension = path.split('.').pop().toLowerCase();
      let mediaType = 'image';
      if (['mp4', 'mov', 'avi'].includes(extension)) {
        mediaType = 'video';
      } else if (['mp3', 'wav'].includes(extension)) {
        mediaType = 'audio';
      } else if (['pdf', 'doc', 'docx', 'xls', 'xlsx'].includes(extension)) {
        mediaType = 'document';
      }
      
      this.viewedMediaPath = this.getMediaUrl(path);
      this.viewedMediaType = mediaType;
      this.showMediaViewerModal = true;
    },

    getVideoMimeType(path) {
      const ext = path.split('.').pop().toLowerCase();
      return {
        'mp4': 'video/mp4',
        'mov': 'video/quicktime',
        'avi': 'video/x-msvideo'
      }[ext] || 'video/mp4';
    },

    getAudioMimeType(path) {
      const ext = path.split('.').pop().toLowerCase();
      return {
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav'
      }[ext] || 'audio/mpeg';
    },

    isGroupAdmin(group) {
      if (!group || !group.members || !this.currentUser?.id) return false;
      const member = group.members.find(m => m.id === this.currentUser.id);
      return member ? member.pivot?.is_admin : false;
    },

    userHasBadge(badgeName) {
      const user = JSON.parse(localStorage.getItem('user')) || {};
      return user.badges?.some(badge => badge.name === badgeName) || false;
    },

    setupFirebaseListeners() {
      window.addEventListener('newGroupMessage', (event) => {
        const message = event.detail;
        if (this.activeGroup && message.group_id === this.activeGroup.id) {
          this.messages.push(message);
          this.$nextTick(this.scrollToBottom);
        }
        
        const group = this.userGroups.find(g => g.id === message.group_id);
        if (group) group.last_message = message;
      });

      window.addEventListener('groupActivity', (event) => {
        const { groupId } = event.detail;
        if (this.activeGroup?.id === groupId) {
          this.selectGroup(this.activeGroup);
        }
        this.fetchGroups();
      });
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutsideEmojiPicker);
  }
};
</script>

<style scoped>

.avatar-image {
  width: 75%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.voice-recording-ui {
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  background-color: #f5f6f8;
  border-radius: 8px;
  margin-bottom: 10px;
}

.recording-indicator {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.pulse-animation {
  width: 12px;
  height: 12px;
  background-color: #f02849;
  border-radius: 50%;
  margin-right: 10px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(240, 40, 73, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(240, 40, 73, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(240, 40, 73, 0);
  }
}

.recording-text {
  font-size: 0.9rem;
  color: #f02849;
  font-weight: 500;
  margin-right: 10px;
}

.recording-duration {
  font-size: 0.9rem;
  color: #65676b;
  font-family: monospace;
}

.recording-controls {
  display: flex;
  justify-content: space-between;
}

.cancel-recording, .send-recording {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-recording {
  background-color: #f0f2f5;
  color: #f02849;
}

.cancel-recording:hover {
  background-color: #e4e6e9;
}

.send-recording {
  background-color: #1877f2;
  color: white;
}

.send-recording:hover {
  background-color: #166fe5;
}

/* Voice button style */
.voice-button {
  background: none;
  border: none;
  color: #65676b;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 5px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.voice-button:hover {
  background-color: #f0f2f5;
  color: #1877f2;
}

/* Adjust input wrapper to include voice button */
.input-wrapper {
  display: flex;
  align-items: center;
  background-color: #ffffff;
  border-radius: 20px;
  padding: 5px 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
/* Base Container */
.groups-container {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  height: calc(100vh - 40px); /* Adjust based on your header/nav height */
}

/* Main Content Wrapper */
.groups-content {
  display: flex;
  height: 100%;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* Sidebar */
.sidebar {
  width: 320px;
  border-right: 1px solid #e9ebee;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
}

.sidebar-header {
  padding: 15px 20px;
  background-color: #f5f6f8;
  border-bottom: 1px solid #e9ebee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sidebar-header h2 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #1c1e21;
}

.add-group-btn {
  background-color: #1877f2;
  border: none;
  transition: all 0.2s;
}

.add-group-btn:hover {
  background-color: #166fe5;
  transform: scale(1.05);
}

.group-list {
  flex: 1;
  overflow-y: auto;
  padding: 5px 0;
}

.group-section {
  margin-bottom: 15px;
}

.section-title {
  font-size: 0.9rem;
  color: #65676b;
  margin: 10px 15px 5px;
  padding: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.group-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 5px;
}

.group-card {
  display: flex;
  padding: 10px 15px;
  border-radius: 0;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  border-left: 3px solid transparent;
}

.group-card:hover {
  background-color: #f5f6f8;
  border-left-color: #4a76a8;
}

.group-card.active {
  background-color: #e6f2ff;
  border-left-color: #1877f2;
}

.group-avatar {
  position: relative;
  margin-right: 12px;
}

.avatar-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #e7f3ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1877f2;
  font-size: 1.1rem;
  overflow: hidden;
}

.avatar-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.unread-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  background-color: #f02849;
  color: white;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  font-weight: bold;
  border: 2px solid white;
}

.group-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.group-details h4 {
  margin: 0 0 2px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1c1e21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group-description {
  margin: 0 0 4px 0;
  font-size: 0.85rem;
  color: #65676b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group-meta {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #65676b;
}

.member-count {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.member-count i {
  margin-right: 3px;
  font-size: 0.7rem;
}

.last-message {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #65676b;
}

.group-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 8px;
}

/* Chat Area */
.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
}

.chat-header {
  padding: 12px 20px;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-left .avatar-circle {
  width: 40px;
  height: 40px;
  margin-right: 12px;
  background-color: #e7f3ff;
  color: #1877f2;
}

.group-info h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1c1e21;
}

.member-count {
  font-size: 0.8rem;
  color: #65676b;
  margin-top: 2px;
}

.member-count span {
  margin-left: 6px;
}

.header-actions {
  display: flex;
  gap: 6px;
}

.header-action-btn {
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 0.8rem;
}

/* Messages Container */
.messages-container {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  background-color: #e5ddd5;
  background-image: url('../../assets/img/logo-school-one.png');
  background-size: 30%;
  background-repeat: no-repeat;
  background-position: center;  
  display: flex;
  flex-direction: column;
}

.message {
  display: flex;
  margin-bottom: 8px;
  position: relative;
  max-width: 80%;
}

.message-content-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.message-content {
  display: inline-block;
  max-width: 100%;
  position: relative;
}

.message-header {
  margin-bottom: 2px;
}

.message-header strong {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1c1e21;
}

.message-body {
  padding: 8px 12px;
  border-radius: 18px;
  background-color: #ffffff;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.1);
  word-wrap: break-word;
  line-height: 1.4;
  font-size: 0.95rem;
}

.message.outgoing .message-body {
  background-color: #dcf8c6;
  border-top-right-radius: 0;
}

.message:not(.outgoing) .message-body {
  border-top-left-radius: 0;
}

.message-time {
  font-size: 0.7rem;
  color: #667781;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 5px;
}

.read-receipt {
  margin-left: 4px;
  color: #4fc3f7;
  font-size: 0.8rem;
}

.message-avatar .avatar-circle {
  width: 32px;
  height: 32px;
  font-size: 0.9rem;
}

.message-actions {
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  padding: 0 5px;
}

.message:hover .message-actions {
  opacity: 1;
}

.message-action-btn {
  color: #65676b;
  transition: color 0.2s;
}

.message-action-btn:hover {
  color: #1877f2;
}

/* Media Messages */
.message-media {
  max-width: 300px;
  max-height: 300px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.message-media:hover {
  transform: scale(1.02);
}

.document-message {
  display: flex;
  align-items: center;
}

/* Message Input Area */
.message-input-area {
  padding: 10px 15px;
  background-color: #f0f2f5;
  border-top: 1px solid #e9ebee;
}

.input-wrapper {
  display: flex;
  align-items: center;
  background-color: #ffffff;
  border-radius: 20px;
  padding: 5px 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.message-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 8px 10px;
  font-size: 0.95rem;
  background: transparent;
  color: #1c1e21;
}

.emoji-button, 
.attachment-button, 
.send-button {
  background: none;
  border: none;
  color: #65676b;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 5px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.emoji-button:hover, 
.attachment-button:hover {
  background-color: #f0f2f5;
  color: #1877f2;
}

.send-button {
  color: #1877f2;
  background-color: #e7f3ff;
}

.send-button:disabled {
  color: #bcc0c4;
  background-color: #f0f2f5;
  cursor: not-allowed;
}

/* Emoji Picker Positioning */
.emoji-picker-container {
  position: absolute;
  bottom: 70px;
  right: 20px;
  z-index: 100;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

/* Empty State */
.empty-chat {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f2f5;
}

.empty-content {
  text-align: center;
  max-width: 400px;
  padding: 20px;
}

.empty-icon {
  font-size: 4rem;
  color: #d9d9d9;
  margin-bottom: 20px;
}

.empty-content h3 {
  font-size: 1.5rem;
  margin-bottom: 10px;
  color: #444;
}

.empty-content p {
  color: #666;
  margin-bottom: 20px;
}

.create-group-btn {
  padding: 10px 20px;
  font-size: 0.9rem;
}

.file-previews {
  padding: 10px 15px;
  background-color: #f5f6f8;
  border-bottom: 1px solid #e9ebee;
}

.file-preview {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
}

.file-preview-content {
  flex: 1;
  display: flex;
  align-items: center;
}

.preview-media {
  max-width: 150px;
  max-height: 150px;
  border-radius: 4px;
  object-fit: contain;
}

.document-preview {
  display: flex;
  align-items: center;
  padding: 8px;
}

.document-preview i {
  font-size: 2rem;
  color: #1877f2;
  margin-right: 10px;
}

.document-preview span {
  font-size: 0.9rem;
  color: #1c1e21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.remove-file-btn {
  background: none;
  border: none;
  color: #65676b;
  font-size: 1rem;
  cursor: pointer;
  padding: 5px;
  margin-left: 10px;
  transition: color 0.2s;
}

.remove-file-btn:hover {
  color: #f02849;
}

/* Responsive Adjustments */
@media (max-width: 1200px) {
  .groups-container {
    padding: 15px;
  }
  
  .sidebar {
    width: 280px;
  }
}

@media (max-width: 992px) {
  .groups-content {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
    height: 40vh;
    border-right: none;
    border-bottom: 1px solid #e9ebee;
  }
}

@media (max-width: 768px) {
  .groups-container {
    padding: 10px;
  }
  
  .message {
    max-width: 90%;
  }
  
  .message-media {
    max-width: 200px;
  }
  
  .header-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  
  .header-action-btn {
    margin-bottom: 5px;
  }

  .preview-media {
    max-width: 100px;
    max-height: 100px;
  }
  
  .document-preview span {
    max-width: 150px;
  }
}
</style>